import {useRouter} from "next/router";
import localized from "../../locale/locales";
import MainLayout from "./MainLayout";
import {useState} from "react";
import Lightbox from "../Lightbox";
import Video from "../Video";
import Container from "../Container";
import Typo from "../Typo";
import VideoThumb from "../VideoThumb";

import TextWithImageAlt from "../TextWithImageAlt";
import Title from "../Title";
import SectionConcepts from "../sections/SectionConcepts";
import SectionTrainers from "../sections/SectionTrainers";
import ScheduleTrial from "../ScheduleTrial";
import SvgLogoLocation from "../../svg/SvgLogoLocation";
import SectionFeatures from "../sections/SectionFeatures";

import iconSkillrow from "../../public/img/icons/ai_black.png";
import iconSkillmill from "../../public/img/icons/star_black.png";
import iconAirbike from "../../public/img/icons/person_black.png";
import Map from "../Map";
import SvgLocationBg from "../../svg/SvgLocationBg";
import Fadeout from "../Fadeout";
import SvgChevronDown from "../../svg/SvgChevronDown";
import MapWithBox from "../MapWithBox";
import {nl2br} from "../../helpers/strings";
import Button from "../Button";
import TextWithVideo from "../TextWithVideo";
import LocationBgComponent from "../LocationBgComponent";
import SectionContactForm from "../sections/SectionContactForm";
import {getTrainers, trainersList} from "../../helpers/trainers";
import {getLocationAddress, getLocationContacts} from "../../helpers/locations";

const icons = [
    iconSkillmill,
    iconSkillrow,
    iconAirbike
];

export default function LocationLayout({
                                           location,
                                           data,
                                           hasCookiePreferences,
                                           cookiePreferences,
                                           hasVideo = false,
                                           showIntro = false,
                                           showFullscreen = false,
                                           showConcepts = false,
                                           showTrainers = false,
                                           showMap = false,
                                           showScheduleTrial = false,
                                           showFeatures = false,
                                           showPhone = true,
                                           trialSlotsResponse
                                       }) {

    const {locale} = useRouter();
    const {common, locations, og} = localized[locale];

    const [hiddenNav, setHiddenNav] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);
    const [conceptsCount, setConceptsCount] = useState(0);
    const [isConceptsOpen, setIsConceptsOpen] = useState(false);

    const locationUpperCase = location.toUpperCase();
    const trainersCount = getTrainers(trainersList, {}, location.toUpperCase()).length;

    if (!locations[location]) {
        return null;
    }

    // todo: refactor this chimpanzee code
    const lang = locations[location];
    const {images, headerVideo, introVideo} = data;

    const locationContacts = getLocationContacts(location);
    const locationAddress = getLocationAddress(location);

    const handleOpenVideoPopup = () => {
        if (hasVideo) {
            setActiveVideo(headerVideo);
            setShowPopup(true);
            setHiddenNav(true);
        }
    }

    const handleCloseVideoPopup = () => {
        if (hasVideo) {
            setShowPopup(false);
            setActiveVideo(null);
            setHiddenNav(false);
        }
    }

    return (
        <MainLayout hasCookiePreferences={hasCookiePreferences}
                    cookiePreferences={cookiePreferences}
                    footerTheme="dark"
                    hiddenNav={hiddenNav}
                    title={lang.og.title}
                    description={lang.og.description}
        >
            {hasVideo && (
                <Lightbox active={showPopup}
                          onClose={handleCloseVideoPopup}
                >
                    <Video url={activeVideo} active={showPopup}/>
                </Lightbox>
            )}

            <div className="overflow-hidden pb-32">
                <section className="bg-black text-white pt-12 min-h-hero-narrow md:h-hero-semibig flex items-center">
                    <Container className="relative flex flex-col md:flex-row gap-10 lg:gap-20 md:items-center">
                        <div
                            className="hidden md:block absolute -top-[40%] left-1/2 w-[115%] h-full -translate-x-1/2 pointer-events-none"
                        >
                            <Fadeout direction="right" color="black" className="w-[100px]"/>
                            <SvgLocationBg className="w-full h-full top-20 relative"/>
                        </div>

                        <div className="md:hidden absolute top-0 left-0 w-screen h-full overflow-hidden">
                            <div
                                className="absolute w-[80%] top-0 left-1/2 -translate-x-1/2 pointer-events-none ease-out transition-transform duration-500"
                            >
                                <LocationBgComponent location={location} width="100%"/>
                            </div>
                        </div>

                        <div className="relative flex-1 lg:flex-[0.7] flex flex-col gap-5">
                            <header className="flex items-start gap-8">
                                <div className="aspect-square h-[33px]">
                                    <SvgLogoLocation id={`location-header-logo-${location}`} location={location}
                                                     className="h-full"/>
                                </div>
                                <div className="flex flex-col gap-2 -mt-1">
                                    <h1 className="text-3.5xl leading-none font-semibold">{locationAddress.street}</h1>
                                    <p className="opacity-70 font-medium text-lg leading-none">{locationAddress.city}</p>
                                </div>
                            </header>
                            <Typo className="max-w-[42ch]">
                                {lang.meta.mapBody}
                            </Typo>

                            {showPhone && locationContacts && locationContacts.phone && (
                                <div className="flex flex-col items-start gap-1 mt-4 md:mt-4">
                                    <Typo>{common.reception}</Typo>
                                    <a href={`tel:${locationContacts.phone.split(' ').join('')}`}
                                       className="inline-flex font-bold text-xl draw-line"
                                    >
                                        {locationContacts.phone}
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="relative flex-1 md:w-full text-black md:top-32 lg:top-24 -mb-24 lg:mb-0">
                            {hasVideo && data.status === 'opened' && (
                                <VideoThumb image={images.header}
                                            showLabel={true}
                                            className="aspect-[7/5]"
                                            onPlay={handleOpenVideoPopup}
                                            fullMobile={false}
                                />
                            )}

                            {!hasVideo && data.status === 'opened' && (
                                <VideoThumb image={images.header}
                                            showLabel={true}
                                            className="aspect-[7/5]"
                                            onPlay={null}
                                            fullMobile={false}
                                />
                            )}

                            {data.status === 'closed' && (
                                <Map location={location}
                                     fullMobile={true}
                                     size="wide"
                                     className="rounded-lg"
                                />
                            )}
                        </div>
                    </Container>
                </section>

            </div>

            <div className="h-32 md:h-0 lg:-mb-16"/>

            {showIntro && (
                <div className="pb-32 lg:pt-48 lg:pb-52 xl:py-52">
                    {introVideo && (
                        <TextWithVideo
                            video={introVideo}
                            childrenClasses="max-w-lg pt-20"
                        >
                            <Title className="text-3xl leading-normal mb-10 max-w-[15ch]">
                                {lang.intro.title}
                            </Title>

                            <Typo className="max-w-[40ch]"
                                  dangerouslySetInnerHTML={{__html: nl2br(lang.intro.body)}}
                            />
                        </TextWithVideo>
                    )}

                    {!introVideo && (
                        <TextWithImageAlt
                            image={images.intro}
                            imageAlt=""
                            imageRatio="aspect-[19/21] max-w-[500px]"
                            childrenClasses="max-w-lg pt-20"
                        >
                            <Title className="text-3xl leading-normal mb-10 max-w-[15ch]">
                                {lang.intro.title}
                            </Title>

                            <Typo className="max-w-[40ch]"
                                  dangerouslySetInnerHTML={{__html: nl2br(lang.intro.body)}}
                            />
                        </TextWithImageAlt>
                    )}
                </div>
            )}

            {showConcepts && (
                <div>
                    <Container className="pb-6 md:pb-20 text-center flex flex-col items-center gap-6">
                        <header>
                            <Title>
                                {lang.concepts.title}
                            </Title>
                        </header>
                        <Typo className="max-w-[70ch]">
                            {lang.concepts.body}
                        </Typo>
                    </Container>

                    <div className="flex flex-col gap-4">
                        <div
                            className={`pt-24 md:pt-32 pb-32 ${conceptsCount > 6 ? `accordion accordion-concepts accordion-solid accordion-xl overflow-hidden ${isConceptsOpen ? 'accordion-open' : ''}` : ''}`}>
                            <SectionConcepts location={locationUpperCase} setConceptsCount={setConceptsCount}/>
                        </div>

                        {conceptsCount > 6 && (
                            <div className="flex w-full items-center">
                                <button
                                    onClick={() => setIsConceptsOpen(!isConceptsOpen)}
                                    className="mx-auto flex gap-2 items-center pt-6 text-xs uppercase text-black border-b border-black mouse-hover:border-transparent transition-colors duration-200"
                                >
                                    {isConceptsOpen ? common.showLessConcepts : common.showAllConcepts}
                                    <SvgChevronDown width={7}
                                                    className={`transform transition-transform ${isConceptsOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}


            {showTrainers && (
                <div className={trainersCount < 7 ? 'mb-32 lg:mb-48' : ''}>
                    <Container
                        className="pt-32 pb-32 md:pb-48 text-center flex flex-col items-center gap-6">
                        <header>
                            <Title>
                                {lang.trainers.title}
                            </Title>
                        </header>
                        <Typo className="max-w-[70ch]">
                            {lang.trainers.body}
                        </Typo>
                    </Container>

                    <div>
                        <SectionTrainers limit={6} location={locationUpperCase}/>
                    </div>
                </div>
            )}

            {showMap && (
                <Container
                    className={`flex flex-col-reverse gap-14 md:flex-row md:items-center md:justify-between ${showTrainers ? 'pt-32' : 'pt-32 sm:pt-48 md:pt-72'} pb-32 md:pb-48`}
                >
                    <div className="relative z-10 flex-1 md:flex-none flex flex-col gap-6">
                        <header>
                            <Title className="max-w-[15ch]">
                                {lang.map.title}
                            </Title>
                        </header>
                        <Typo className="max-w-[41ch]">
                            {lang.map.body}
                        </Typo>
                        <address className="not-italic font-semibold text-sm">
                            {locationAddress.street}<br/>
                            {locationAddress.city}
                        </address>
                        <div className="mt-4">
                            <Button href="/#memberships"
                                    label={common.memberships}
                            />
                        </div>
                    </div>
                    <div className="relative flex-1 max-w-[600px] -mx-6 sm:mx-0">
                        <MapWithBox location={location} fullMobile={true} className="md:top-32 relative"/>
                    </div>
                </Container>
            )}

            {showFeatures && (
                <Container className="flex flex-col items-center gap-16 py-36">
                    <header className="text-center">
                        <Title>
                            {lang.features.title}
                        </Title>
                    </header>
                    <SectionFeatures
                        theme="black-on-white"
                        icons={icons}
                        items={lang.features.items.map(({title, body}, key) => {
                            return {
                                title,
                                body,
                                // icon: icons[key]
                            }
                        })}
                    />
                </Container>
            )}

            {showScheduleTrial && (
                <ScheduleTrial
                    initialSlots={trialSlotsResponse}
                    setHiddenNav={setHiddenNav}
                    className="pb-32"
                    location={location}
                />
            )}

            <SectionContactForm/>
        </MainLayout>
    )
}
